import { Box, Icon, Typography } from "@outschool/backpack";
import { faInfoCircle } from "@outschool/icons";
import * as Localization from "@outschool/localization";
import { Trans, useLocale, useTranslation } from "@outschool/localization";
import { parentSettingsPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { useTopNoticeContext } from "@outschool/ui-components-website";
import {
  HoverTooltip,
  Loading,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import { useIsTablet } from "@outschool/ui-utils";
import React, { useEffect, useState } from "react";

import { ENABLE_LOCALE_PSEUDOLOCALIZATION } from "../../../shared/Env";
import { CurrencyCode } from "../../../shared/generated/graphql";
import { usePseudoLocalization, useSetLocale } from "../../hooks/Localization";
import { shouldChargeInLocalCurrency } from "../../lib/Localization";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import Link from "../Link";
import CurrencySelector from "./CurrencySelector";
import LanguageSelector from "./LanguageSelector";
import LocalizationPickerMenuApplyAndCancel from "./LocalizationPickerMenuApplyAndCancel";
import PseudoLocalizationControl from "./PseudoLocalizationControl";
import TimeZoneSelector from "./TimeZoneSelector";

interface LocalizationPickerMenuProps {
  isOpen?: boolean;
  onClose?: () => void;
  heading?: string;
}

export default function LocalizationPickerMenu({
  isOpen,
  onClose,
  heading
}: LocalizationPickerMenuProps) {
  const { t } = useTranslation(
    "client\\components\\localization\\LocalizationPickerMenu"
  );
  heading ??= t`Update regional settings`;
  const { isLoggedIn } = useSession();
  const { setCurrency } = Localization.useSetCurrencyLocalization();
  const { currencyCode } = Localization.useCurrencyLocalization();
  const { timeZone, setTimeZone } = useTimeZone();
  const locale = useLocale();
  const { setLocale } = useSetLocale();
  const { togglePseudo, pseudoRepeatCount, updatePseudoLocalizationSettings } =
    usePseudoLocalization();

  const [selectedCurrency, setSelectedCurrency] =
    useState<CurrencyCode>(currencyCode);
  const [selectedTimeZone, setSelectedTimeZone] = useState<string>(timeZone);
  const [selectedLanguage, setSelectedLanguage] =
    useState<Localization.SupportedLocale>(locale);
  const [togglePseudolocalization, setTogglePseudolocalization] =
    useState(togglePseudo);
  const [pseudolocalizationRepeatCount, setPseudolocalizationRepeatCount] =
    useState(pseudoRepeatCount);

  const [applyIsLoading, setApplyIsLoading] = useState<Boolean>(false);

  const isTablet = useIsTablet();

  useEffect(() => {
    setSelectedCurrency(currencyCode);
  }, [currencyCode]);

  const isChargingInLocalCurrency =
    shouldChargeInLocalCurrency(selectedCurrency);

  const [error, setError] = useState<string | null>(null);

  const { setTimedTopNotice } = useTopNoticeContext();

  if (error) {
    setTimedTopNotice(error);
  }

  const trackingLabelApply = "localization_picker_menu_apply";

  const platformView = isTablet ? "mobile" : "desktop";

  const { trackTouch: trackApplyTouch } = useImpressionTracking({
    trackingLabel: trackingLabelApply,
    uniqueId: trackingLabelApply,
    sharedProperties: {
      currencyCode: selectedCurrency,
      timeZone: selectedTimeZone,
      platformView
    }
  });

  const trackingLabelCancel = "localization_picker_menu_cancel";

  const { trackTouch: trackCancelTouch } = useImpressionTracking({
    trackingLabel: trackingLabelCancel,
    uniqueId: trackingLabelCancel,
    sharedProperties: {
      platformView
    }
  });

  const onApply = async () => {
    try {
      setApplyIsLoading(true);
      await setCurrency(selectedCurrency);
      await setTimeZone(selectedTimeZone);
      // Call this last, since it may redirect the page
      setLocale(selectedLanguage);
      // updates context and i18n with local state
      updatePseudoLocalizationSettings(
        togglePseudolocalization,
        pseudolocalizationRepeatCount
      );
      setApplyIsLoading(false);
    } catch (err) {
      setError(err);
      console.error(err);
    }
  };

  const cancel = () => {
    setSelectedCurrency(currencyCode);
  };

  const handleClickApply = async () => {
    trackApplyTouch();
    await onApply();
    // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    onClose();
  };

  const handleClickCancel = () => {
    cancel();
    // @ts-ignore TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
    onClose();
    trackCancelTouch();
  };

  const currencySelectorProps = {
    hideCurrencyDisclaimer: isChargingInLocalCurrency,
    selectedCurrency,
    setSelectedCurrency
  };
  const timeZoneSelectorProps = {
    selectedTimeZone,
    setSelectedTimeZone
  };
  const languageSelectorProps = {
    selectedLanguage,
    setSelectedLanguage
  };
  const pseudoControlProps = {
    contextToggleValue: togglePseudo,
    togglePseudolocalization,
    pseudolocalizationRepeatCount,
    setTogglePseudolocalization,
    setPseudolocalizationRepeatCount
  };

  const headingSx = {
    margin: "15px 0 10px 0"
  };

  const subheadText = isLoggedIn
    ? t`Select your time zone, the language you speak, and the currency you use`
    : t`Select the language you speak and the currency you use`;

  return (
    <>
      <Box
        sx={{
          padding: "15px"
        }}
        id="localization-picker-menu"
      >
        <Typography
          variant="h3"
          sx={{
            marginBottom: "10px"
          }}
        >
          {heading}
        </Typography>
        <Typography
          variant="inherit"
          sx={{
            display: "inline-block",
            maxWidth: "330px"
          }}
        >
          {subheadText}
        </Typography>
        <React.Suspense fallback={<Loading />}>
          <Typography variant="h4" sx={headingSx}>
            {t`Language`} <LanguageTooltip t={t} />
          </Typography>
          <LanguageSelector {...languageSelectorProps} />
        </React.Suspense>
        <Typography variant="h4" sx={headingSx}>
          {t`Currency`}
        </Typography>
        <CurrencySelector {...currencySelectorProps} />
        <React.Suspense fallback={<Loading />}>
          <Typography variant="h4" sx={headingSx}>
            {t`Time Zone`}
          </Typography>
          <TimeZoneSelector {...timeZoneSelectorProps} />
        </React.Suspense>
      </Box>
      {ENABLE_LOCALE_PSEUDOLOCALIZATION && (
        <Box
          sx={{
            padding: "15px",
            boxShadow: "0 0 1px 3px rgba(0, 0, 0, .125)",
            bg: "#F0F3FF"
          }}
          id="toggle_pseudolocalization"
        >
          <PseudoLocalizationControl {...pseudoControlProps} />
        </Box>
      )}
      <LocalizationPickerMenuApplyAndCancel
        isOpen={isOpen}
        handleClickApply={handleClickApply}
        handleClickCancel={handleClickCancel}
        applyIsLoading={applyIsLoading}
      />
    </>
  );
}

const LanguageTooltip = ({ t }) => (
  <HoverTooltip
    shouldShowTooltip={true}
    tooltipPlacement="top"
    tooltipConfig={{
      interactive: true,
      delayHide: 300,
      delayShow: 200
    }}
    tooltipStyle="peacock-outline"
    tooltipText={
      <Typography
        variant="inherit"
        sx={{
          fontSize: "initial",
          fontWeight: "fontWeightRegular"
        }}
      >
        <Trans t={t}>
          The language the site is currently displayed in. To update your
          default language for emails,{" "}
          <Link to={parentSettingsPath()} reloadDocument={true}>
            visit your settings page
          </Link>
          .
        </Trans>
      </Typography>
    }
  >
    <Icon
      icon={faInfoCircle}
      color="primary"
      sx={{
        fontSize: "0.7em"
      }}
    />
  </HoverTooltip>
);
